/**
 * Form plumbing shared by the scoring components. Kept out of the component files so fast refresh
 * keeps working (a component module must only export components).
 */

import { z } from 'zod'
import type { ScoringKnobDto, ScoringOptionKey } from '@api/types/scoring'

/** "Scoring.RepeatDecayBase" -> "repeatDecayBase" (the DTO field name). */
export function fieldForKey(key: string): ScoringOptionKey {
  const name = key.startsWith('Scoring.') ? key.slice('Scoring.'.length) : key
  return (name.charAt(0).toLowerCase() + name.slice(1)) as ScoringOptionKey
}

/**
 * Builds the validation schema from the server's own knob metadata, so the client can't disagree
 * with the policy about what's legal. The server re-validates regardless — this is for fast feedback.
 */
export function buildScoringSchema(knobs: ScoringKnobDto[]) {
  const shape: Record<string, z.ZodTypeAny> = {}

  for (const knob of knobs) {
    // MUI number inputs emit strings; coerce, or every field fails as "expected number".
    let rule = z.coerce
      .number({ invalid_type_error: 'Must be a number' })
      .min(knob.min, `Must be at least ${knob.min}`)
      .max(knob.max, `Must be at most ${knob.max}`)

    if (knob.dataType === 'integer') {
      rule = rule.int('Must be a whole number')
    }

    shape[fieldForKey(knob.key)] = rule
  }

  return z.object(shape)
}
